import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, FlaskConical, Plus, Save, Trash2 } from 'lucide-react';
import { DEFAULT_POLICY_FORM, PolicyFormState, buildPolicyJson } from '../types';
import {
  GovernanceBreadcrumbs,
  SectionCard,
  FormField,
  ToggleSwitch,
  btnPrimary,
  btnSecondary,
  inputClass,
  selectClass,
} from '../components/shared';
import TestPolicyModal from '../components/TestPolicyModal';
import { aiGovernanceService, GroupItem, PolicyDetail } from '../../../../components/api/aiGovernanceService';

const SECTIONS = [
  'Basic Information',
  'Prompt Firewall',
  'Data Protection',
  'Resource Governance',
  'Authorization Conditions',
  'Approval Rules',
  'Policy Overrides',
  'Audit & Compliance',
  'Output Validation',
];

const APPLIES_TO = ['Chatbots', 'Agents', 'Workflows', 'Knowledge Bases', 'LLMs', 'MCP Tools', 'Vector Stores', 'Data Connections'];
const SENSITIVE_TYPES = ['PII', 'Financial Data', 'HR Data', 'Legal Data', 'PHI / Healthcare Data', 'PCI Data', 'Secrets / Credentials'];
const COMPLIANCE = ['GDPR', 'SOX', 'HIPAA', 'PCI-DSS', 'ISO 27001', 'SOC 2', 'EU AI Act', 'NIST AI RMF'];
const CONDITION_FIELDS = ['user.role', 'user.department', 'user.group', 'resource.type', 'resource.classification', 'request.intent', 'request.risk_level', 'data.field', 'data.classification', 'workflow.name', 'tool.name', 'model.provider'];
const OVERRIDE_SCOPES = ['User', 'Group', 'Department', 'Resource', 'Workflow', 'Knowledge Base'];

function detailToForm(detail: PolicyDetail): PolicyFormState {
  return {
    ...DEFAULT_POLICY_FORM,
    name: detail.name || '',
    code: detail.code || '',
    description: detail.description || '',
    status: (detail.status as PolicyFormState['status']) || 'Draft',
    group: detail.groupId || detail.group || '',
    protectionLevel: (detail.protectionLevel as PolicyFormState['protectionLevel']) || 'Standard',
    appliesTo: detail.appliesTo || DEFAULT_POLICY_FORM.appliesTo,
    promptFirewall: { ...DEFAULT_POLICY_FORM.promptFirewall, ...(detail.promptFirewall as object) } as PolicyFormState['promptFirewall'],
    dataProtection: { ...DEFAULT_POLICY_FORM.dataProtection, ...(detail.dataProtection as object) } as PolicyFormState['dataProtection'],
    resourceGovernance: { ...DEFAULT_POLICY_FORM.resourceGovernance, ...(detail.resourceGovernance as object) } as PolicyFormState['resourceGovernance'],
    authConditions: (detail.authorizationConditions || DEFAULT_POLICY_FORM.authConditions).map((c, i) => ({
      id: (c as { id?: string }).id || String(i + 1),
      field: (c as { field?: string }).field || 'user.role',
      operator: (c as { operator?: string }).operator || 'equals',
      value: (c as { value?: string }).value || '',
      action: (c as { action?: string }).action || 'allow',
    })),
    approvalRules: { ...DEFAULT_POLICY_FORM.approvalRules, ...(detail.approvalRules as object) } as PolicyFormState['approvalRules'],
    overrides: { ...DEFAULT_POLICY_FORM.overrides, ...(detail.overrides as object) } as PolicyFormState['overrides'],
    audit: { ...DEFAULT_POLICY_FORM.audit, ...(detail.audit as object) } as PolicyFormState['audit'],
    outputValidation: { ...DEFAULT_POLICY_FORM.outputValidation, ...(detail.outputValidation as object) } as PolicyFormState['outputValidation'],
  };
}

function formToPayload(form: PolicyFormState) {
  const json = buildPolicyJson(form);
  return {
    name: form.name,
    code: form.code,
    description: form.description,
    status: form.status,
    groupId: form.group,
    type: 'Runtime Enforcement',
    protectionLevel: form.protectionLevel,
    riskLevel: form.overrides.riskLevel || 'Medium',
    appliesTo: form.appliesTo,
    promptFirewall: form.promptFirewall,
    dataProtection: form.dataProtection,
    resourceGovernance: form.resourceGovernance,
    authorizationConditions: form.authConditions,
    approvalRules: form.approvalRules,
    overrides: form.overrides,
    audit: form.audit,
    outputValidation: form.outputValidation,
    policy_id: json.policy_id,
  };
}

export default function PolicyCreateForm() {
  const navigate = useNavigate();
  const { policyId } = useParams<{ policyId: string }>();
  const isEdit = Boolean(policyId);
  const [form, setForm] = useState<PolicyFormState>(DEFAULT_POLICY_FORM);
  const [activeSection, setActiveSection] = useState(0);
  const [showTest, setShowTest] = useState(false);
  const [groups, setGroups] = useState<GroupItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);

  const policyJson = useMemo(() => buildPolicyJson(form), [form]);

  useEffect(() => {
    (async () => {
      try {
        const res = await aiGovernanceService.getGroups({ page: 1, pageSize: 100 });
        setGroups(res.items || []);
        if (!isEdit && res.items?.length && !form.group) {
          setForm((prev) => ({ ...prev, group: res.items[0].id }));
        }
      } catch {
        // ignore
      }
    })();
  }, [isEdit]);

  useEffect(() => {
    if (!policyId) return;
    (async () => {
      setLoading(true);
      try {
        const detail = await aiGovernanceService.getPolicy(policyId);
        setForm(detailToForm(detail));
      } catch {
        setError('Failed to load policy.');
      } finally {
        setLoading(false);
      }
    })();
  }, [policyId]);

  const update = <K extends keyof PolicyFormState>(key: K, value: PolicyFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const toggleAppliesTo = (item: string) => {
    setForm((prev) => ({
      ...prev,
      appliesTo: prev.appliesTo.includes(item)
        ? prev.appliesTo.filter((a) => a !== item)
        : [...prev.appliesTo, item],
    }));
  };

  const toggleSensitiveType = (item: string) => {
    setForm((prev) => ({
      ...prev,
      dataProtection: {
        ...prev.dataProtection,
        sensitiveTypes: prev.dataProtection.sensitiveTypes.includes(item)
          ? prev.dataProtection.sensitiveTypes.filter((t) => t !== item)
          : [...prev.dataProtection.sensitiveTypes, item],
      },
    }));
  };

  const toggleCompliance = (item: string) => {
    setForm((prev) => ({
      ...prev,
      audit: {
        ...prev.audit,
        complianceMappings: prev.audit.complianceMappings.includes(item)
          ? prev.audit.complianceMappings.filter((c) => c !== item)
          : [...prev.audit.complianceMappings, item],
      },
    }));
  };

  const addCondition = () => {
    setForm((prev) => ({
      ...prev,
      authConditions: [
        ...prev.authConditions,
        { id: Date.now().toString(), field: 'user.role', operator: 'equals', value: '', action: 'allow' },
      ],
    }));
  };

  const removeCondition = (id: string) => {
    setForm((prev) => ({
      ...prev,
      authConditions: prev.authConditions.filter((c) => c.id !== id),
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.code.trim()) {
      setError('Policy name and code are required.');
      return;
    }
    if (!form.group) {
      setError('Please select a policy group.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = formToPayload(form);
      if (isEdit && policyId) {
        await aiGovernanceService.updatePolicy(policyId, payload);
      } else {
        await aiGovernanceService.createPolicy(payload);
      }
      navigate('/portal/ai-governance/policies');
    } catch {
      setError('Failed to save policy. Ensure the code is unique and the group exists.');
    } finally {
      setSaving(false);
    }
  };

  const renderSection = () => {
    switch (activeSection) {
      case 0:
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Policy Name">
                <input value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="e.g. Financial Strict Policy" className={inputClass} />
              </FormField>
              <FormField label="Policy Code">
                <input value={form.code} onChange={(e) => update('code', e.target.value)} placeholder="e.g. financial_strict_v1" className={`${inputClass} font-mono`} disabled={isEdit} />
              </FormField>
            </div>
            <FormField label="Description">
              <textarea value={form.description} onChange={(e) => update('description', e.target.value)} rows={2} className={inputClass} placeholder="Describe the policy purpose and scope..." />
            </FormField>
            <div className="grid grid-cols-3 gap-3">
              <FormField label="Status">
                <select value={form.status} onChange={(e) => update('status', e.target.value as PolicyFormState['status'])} className={selectClass}>
                  <option>Active</option><option>Draft</option><option>Inactive</option>
                </select>
              </FormField>
              <FormField label="Policy Group">
                <select value={form.group} onChange={(e) => update('group', e.target.value)} className={selectClass}>
                  <option value="">Select group</option>
                  {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </FormField>
              <FormField label="Protection Level">
                <select value={form.protectionLevel} onChange={(e) => update('protectionLevel', e.target.value as PolicyFormState['protectionLevel'])} className={selectClass}>
                  <option>Standard</option><option>Strict</option><option>Maximum</option>
                </select>
              </FormField>
            </div>
            <FormField label="Applies To">
              <div className="flex flex-wrap gap-1.5">
                {APPLIES_TO.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => toggleAppliesTo(item)}
                    className={`px-2 py-1 rounded text-[10px] font-medium border transition-colors ${
                      form.appliesTo.includes(item)
                        ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </FormField>
          </div>
        );

      case 1:
        return (
          <div className="space-y-1">
            <ToggleSwitch label="Enable Prompt Firewall" checked={form.promptFirewall.enabled} onChange={(v) => update('promptFirewall', { ...form.promptFirewall, enabled: v })} />
            {[
              ['blockPromptInjection', 'Block Prompt Injection'],
              ['blockJailbreak', 'Block Jailbreak Attempts'],
              ['detectHiddenInstructions', 'Detect Hidden Instructions'],
              ['detectPromptLeakage', 'Detect Prompt Leakage'],
              ['detectCredentialExtraction', 'Detect Credential Extraction'],
              ['detectUnsafeSql', 'Detect Unsafe SQL Generation'],
              ['detectMalwareRequests', 'Detect Malware Requests'],
              ['detectDataExfiltration', 'Detect Data Exfiltration Attempts'],
            ].map(([key, label]) => (
              <ToggleSwitch
                key={key}
                label={label}
                checked={form.promptFirewall[key as keyof typeof form.promptFirewall] as boolean}
                onChange={(v) => update('promptFirewall', { ...form.promptFirewall, [key]: v })}
              />
            ))}
            <div className="mt-4 pt-3 border-t border-slate-100">
              <p className="text-[11px] font-semibold text-slate-700 mb-2">Risk Action Mapping</p>
              {Object.entries(form.promptFirewall.riskActions).map(([risk, action]) => (
                <div key={risk} className="flex items-center gap-2 mb-1.5">
                  <span className="text-[10px] text-slate-500 w-16">{risk}</span>
                  <span className="text-slate-300">→</span>
                  <select
                    value={action}
                    onChange={(e) => update('promptFirewall', {
                      ...form.promptFirewall,
                      riskActions: { ...form.promptFirewall.riskActions, [risk]: e.target.value },
                    })}
                    className={`${selectClass} flex-1`}
                  >
                    <option>Allow</option>
                    <option>Allow + Audit</option>
                    <option>Ask Confirmation</option>
                    <option>Block + Audit</option>
                    <option>Block + Notify Security Admin</option>
                  </select>
                </div>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-3">
            <FormField label="Data Classification">
              <select value={form.dataProtection.classification} onChange={(e) => update('dataProtection', { ...form.dataProtection, classification: e.target.value })} className={selectClass}>
                <option>Public</option><option>Internal</option><option>Confidential</option><option>Restricted</option>
              </select>
            </FormField>
            <FormField label="Sensitive Data Types">
              <div className="flex flex-wrap gap-1.5">
                {SENSITIVE_TYPES.map((t) => (
                  <button key={t} type="button" onClick={() => toggleSensitiveType(t)} className={`px-2 py-1 rounded text-[10px] font-medium border transition-colors ${form.dataProtection.sensitiveTypes.includes(t) ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-white text-slate-600 border-slate-200'}`}>
                    {t}
                  </button>
                ))}
              </div>
            </FormField>
            <div className="border-t border-slate-100 pt-2 space-y-0">
              {[
                ['maskSensitiveData', 'Mask Sensitive Data'],
                ['redactPii', 'Redact PII'],
                ['preventBulkExport', 'Prevent Bulk Export'],
                ['requireCitations', 'Require Citations'],
                ['blockExternalModel', 'Block External Model Usage for Restricted Data'],
                ['encryptStoredContext', 'Encrypt Stored Context'],
                ['enableRetentionRules', 'Enable Retention Rules'],
              ].map(([key, label]) => (
                <ToggleSwitch key={key} label={label} checked={form.dataProtection[key as keyof typeof form.dataProtection] as boolean} onChange={(v) => update('dataProtection', { ...form.dataProtection, [key]: v })} />
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-3">
            <p className="text-[10px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
              Policies control what is allowed at runtime. Access controls define who can use the resource.
            </p>
            {([
              ['allowedLlms', 'Allowed LLMs', 'GPT-4o, Claude 3.5, GPT-4o-mini'],
              ['allowedKnowledgeBases', 'Allowed Knowledge Bases', 'Finance KB, HR KB'],
              ['allowedVectorStores', 'Allowed Vector Stores', 'pinecone-prod, weaviate-dev'],
              ['allowedMcpTools', 'Allowed MCP Tools', 'sql_query, document_search'],
              ['allowedWorkflows', 'Allowed Workflows', 'Contract Review, Invoice Processing'],
              ['allowedChatbots', 'Allowed Chatbots / Agents', 'Finance Copilot, HR Assistant'],
              ['allowedDataConnections', 'Allowed Data Connections', 'Salesforce, Snowflake'],
            ] as const).map(([key, label, placeholder]) => (
              <FormField key={key} label={label}>
                <input
                  value={form.resourceGovernance[key].join(', ')}
                  onChange={(e) => update('resourceGovernance', {
                    ...form.resourceGovernance,
                    [key]: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  })}
                  placeholder={placeholder}
                  className={inputClass}
                />
              </FormField>
            ))}
          </div>
        );

      case 4:
        return (
          <div className="space-y-3">
            <p className="text-[10px] text-slate-500">Build authorization rules using IF / AND / THEN logic.</p>
            {form.authConditions.map((cond, idx) => (
              <div key={cond.id} className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg p-2">
                <span className="text-[10px] font-mono text-indigo-600 w-8">{idx === 0 ? 'IF' : 'AND'}</span>
                <select value={cond.field} onChange={(e) => {
                  const updated = form.authConditions.map((c) => c.id === cond.id ? { ...c, field: e.target.value } : c);
                  update('authConditions', updated);
                }} className={`${selectClass} flex-1`}>
                  {CONDITION_FIELDS.map((f) => <option key={f}>{f}</option>)}
                </select>
                <select value={cond.operator} onChange={(e) => {
                  const updated = form.authConditions.map((c) => c.id === cond.id ? { ...c, operator: e.target.value } : c);
                  update('authConditions', updated);
                }} className={`${selectClass} w-24`}>
                  <option>equals</option><option>not equals</option><option>contains</option><option>in</option>
                </select>
                <input value={cond.value} onChange={(e) => {
                  const updated = form.authConditions.map((c) => c.id === cond.id ? { ...c, value: e.target.value } : c);
                  update('authConditions', updated);
                }} placeholder="Value" className={`${inputClass} flex-1`} />
                <span className="text-[10px] font-mono text-indigo-600">THEN</span>
                <select value={cond.action} onChange={(e) => {
                  const updated = form.authConditions.map((c) => c.id === cond.id ? { ...c, action: e.target.value } : c);
                  update('authConditions', updated);
                }} className={`${selectClass} w-28`}>
                  <option>allow</option><option>mask</option><option>deny</option><option>require approval</option>
                </select>
                <button onClick={() => removeCondition(cond.id)} className="p-1 text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <button onClick={addCondition} className={btnSecondary}><Plus className="w-3.5 h-3.5" /> Add Condition</button>
          </div>
        );

      case 5:
        return (
          <div className="space-y-1">
            {([
              ['highRiskPrompts', 'Require approval for high-risk prompts'],
              ['bulkExport', 'Require approval for bulk export'],
              ['restrictedDataAccess', 'Require approval for restricted data access'],
              ['externalModelUsage', 'Require approval for external model usage'],
              ['financialThreshold', 'Require approval for financial action above threshold'],
            ] as const).map(([key, label]) => (
              <ToggleSwitch key={key} label={label} checked={form.approvalRules[key]} onChange={(v) => update('approvalRules', { ...form.approvalRules, [key]: v })} />
            ))}
            <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-slate-100">
              <FormField label="Approver Role">
                <select value={form.approvalRules.approverRole} onChange={(e) => update('approvalRules', { ...form.approvalRules, approverRole: e.target.value })} className={selectClass}>
                  <option>Security Admin</option><option>Department Manager</option><option>CISO</option><option>Compliance Officer</option>
                </select>
              </FormField>
              <FormField label="Expiration Time">
                <select value={form.approvalRules.expirationTime} onChange={(e) => update('approvalRules', { ...form.approvalRules, expirationTime: e.target.value })} className={selectClass}>
                  <option>1 hour</option><option>4 hours</option><option>24 hours</option><option>72 hours</option>
                </select>
              </FormField>
              <FormField label="Escalation Rule">
                <input value={form.approvalRules.escalationRule} onChange={(e) => update('approvalRules', { ...form.approvalRules, escalationRule: e.target.value })} className={inputClass} />
              </FormField>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-3">
            <ToggleSwitch label="Allow temporary exception" checked={form.overrides.allowTemporary} onChange={(v) => update('overrides', { ...form.overrides, allowTemporary: v })} />
            <FormField label="Exception Scope">
              <div className="flex flex-wrap gap-1.5">
                {OVERRIDE_SCOPES.map((s) => (
                  <button key={s} type="button" onClick={() => {
                    const scopes = form.overrides.scopes.includes(s) ? form.overrides.scopes.filter((x) => x !== s) : [...form.overrides.scopes, s];
                    update('overrides', { ...form.overrides, scopes });
                  }} className={`px-2 py-1 rounded text-[10px] font-medium border transition-colors ${form.overrides.scopes.includes(s) ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-white text-slate-600 border-slate-200'}`}>
                    {s}
                  </button>
                ))}
              </div>
            </FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Reason"><input value={form.overrides.reason} onChange={(e) => update('overrides', { ...form.overrides, reason: e.target.value })} className={inputClass} /></FormField>
              <FormField label="Approver"><input value={form.overrides.approver} onChange={(e) => update('overrides', { ...form.overrides, approver: e.target.value })} className={inputClass} /></FormField>
              <FormField label="Expiry Date"><input type="date" value={form.overrides.expiryDate} onChange={(e) => update('overrides', { ...form.overrides, expiryDate: e.target.value })} className={inputClass} /></FormField>
              <FormField label="Risk Level">
                <select value={form.overrides.riskLevel} onChange={(e) => update('overrides', { ...form.overrides, riskLevel: e.target.value as PolicyFormState['overrides']['riskLevel'] })} className={selectClass}>
                  <option>Low</option><option>Medium</option><option>High</option><option>Critical</option>
                </select>
              </FormField>
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-3">
            <ToggleSwitch label="Enable audit logging" checked={form.audit.enabled} onChange={(v) => update('audit', { ...form.audit, enabled: v })} />
            {([
              ['logPrompts', 'Log prompts'],
              ['logResponses', 'Log responses'],
              ['logPolicyDecisions', 'Log policy decisions'],
              ['logToolCalls', 'Log tool calls'],
              ['logRetrievedDocuments', 'Log retrieved documents'],
              ['logBlockedRequests', 'Log blocked requests'],
            ] as const).map(([key, label]) => (
              <ToggleSwitch key={key} label={label} checked={form.audit[key]} onChange={(v) => update('audit', { ...form.audit, [key]: v })} />
            ))}
            <FormField label="Compliance Mappings">
              <div className="flex flex-wrap gap-1.5">
                {COMPLIANCE.map((c) => (
                  <button key={c} type="button" onClick={() => toggleCompliance(c)} className={`px-2 py-1 rounded text-[10px] font-medium border transition-colors ${form.audit.complianceMappings.includes(c) ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-white text-slate-600 border-slate-200'}`}>
                    {c}
                  </button>
                ))}
              </div>
            </FormField>
          </div>
        );

      case 8:
        return (
          <div className="space-y-1">
            {([
              ['scanBeforeSend', 'Scan response before sending to user'],
              ['maskPiiInResponse', 'Mask PII in response'],
              ['preventPromptLeakage', 'Prevent prompt leakage'],
              ['preventConfidentialLeakage', 'Prevent confidential data leakage'],
              ['detectToxicContent', 'Detect toxic or unsafe content'],
              ['requireSourceCitations', 'Require source citations'],
              ['blockOnViolation', 'Block response if policy violation is detected'],
            ] as const).map(([key, label]) => (
              <ToggleSwitch key={key} label={label} checked={form.outputValidation[key]} onChange={(v) => update('outputValidation', { ...form.outputValidation, [key]: v })} />
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return <div className="p-8 text-sm text-slate-500">Loading policy...</div>;
  }

  return (
    <div className="space-y-3">
      <GovernanceBreadcrumbs items={[
        { label: 'AI Governance', href: '/portal/ai-governance/policies' },
        { label: 'Policies', href: '/portal/ai-governance/policies' },
        { label: isEdit ? 'Edit Policy' : 'Create Policy' },
      ]} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">{isEdit ? 'Edit Policy' : 'Create Policy'}</h1>
          <p className="text-xs text-slate-500">Configure a comprehensive AI governance policy with runtime enforcement rules.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowTest(true)} className={btnSecondary} disabled={!isEdit}><FlaskConical className="w-3.5 h-3.5" /> Test Policy</button>
          <button onClick={handleSave} disabled={saving} className={btnPrimary}><Save className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save Policy'}</button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">{error}</div>}
      {!isEdit && <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">Save the policy first to enable Test Policy simulation.</p>}

      <div className="flex gap-4">
        <div className="flex-1 min-w-0 space-y-3">
          <div className="flex flex-wrap gap-1">
            {SECTIONS.map((section, i) => (
              <button
                key={section}
                onClick={() => setActiveSection(i)}
                className={`px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                  activeSection === i ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                }`}
              >
                {i + 1}. {section}
              </button>
            ))}
          </div>

          <SectionCard title={SECTIONS[activeSection]} description={`Section ${activeSection + 1} of ${SECTIONS.length}`}>
            {renderSection()}
          </SectionCard>

          <div className="flex items-center justify-between">
            <button
              onClick={() => activeSection > 0 ? setActiveSection(activeSection - 1) : navigate('/portal/ai-governance/policies')}
              className={btnSecondary}
            >
              <ChevronLeft className="w-3.5 h-3.5" /> {activeSection === 0 ? 'Cancel' : 'Previous'}
            </button>
            <button
              onClick={() => activeSection < SECTIONS.length - 1 ? setActiveSection(activeSection + 1) : undefined}
              className={btnPrimary}
              disabled={activeSection === SECTIONS.length - 1}
            >
              Next <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <div className="w-80 shrink-0 hidden lg:block">
          <div className="sticky top-4">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <div className="px-3 py-2 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">JSON Preview</span>
                <span className="text-[10px] font-medium text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">Live</span>
              </div>
              <pre className="p-3 text-[10px] text-slate-700 font-mono overflow-auto max-h-[calc(100vh-12rem)] leading-relaxed bg-slate-50/80">
                {JSON.stringify(policyJson, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      </div>

      {showTest && policyId && <TestPolicyModal policyId={policyId} onClose={() => setShowTest(false)} />}
    </div>
  );
}
